export type Stop = [number, string]

export const getColorBasedOnStops = (
  value: number,
  stops: Array<Stop>,
  interpolateColors: boolean,
) => {
  // Sort the stops array by threshold value in ascending order
  stops.sort((a, b) => a[0] - b[0])

  // If the value is below the first stop's threshold, return the first stop's color immediately
  if (stops.length > 0 && value < stops[0][0]) {
    return stops[0][1]
  }

  let currentColor = null
  // Iterate over the stops to find the appropriate color or to interpolate
  for (let i = 0; i < stops.length; i++) {
    const currentStop = stops[i]
    const nextStop = stops[i + 1] // Safely reference next stop

    if (value < currentStop[0]) {
      break // If value is less than the first stop, stop processing
    }

    if (!interpolateColors || !nextStop || value <= currentStop[0]) {
      currentColor = currentStop[1] // No interpolation or no next stop
    } else if (interpolateColors && value < nextStop[0]) {
      // Perform interpolation
      const range = nextStop[0] - currentStop[0]
      const factor = (value - currentStop[0]) / range
      currentColor = interpolateColor(currentStop[1], nextStop[1], factor)
      break // We've determined the color, so break out of the loop
    }
  }

  // If no color was set and there are stops defined, return the last stop's color
  if (currentColor === null && stops.length > 0) {
    currentColor = stops[stops.length - 1][1]
  }

  return currentColor
}

const interpolateColor = (color1: string, color2: string, factor: number): string => {
  // Simple linear interpolation between two colors
  const hex = (color: string) => parseInt(color.slice(1), 16)
  const r = (color: number) => (color >> 16) & 0xff
  const g = (color: number) => (color >> 8) & 0xff
  const b = (color: number) => color & 0xff

  const color1Hex = hex(color1)
  const color2Hex = hex(color2)

  const red = Math.round(r(color1Hex) + factor * (r(color2Hex) - r(color1Hex)))
  const green = Math.round(g(color1Hex) + factor * (g(color2Hex) - g(color1Hex)))
  const blue = Math.round(b(color1Hex) + factor * (b(color2Hex) - b(color1Hex)))

  const rgb = (red << 16) | (green << 8) | blue
  return `#${rgb.toString(16).padStart(6, '0')}`
}
